var { getCookie } = require("./utm-tracking")

// Parse a UTM-style query string ("?utm_source=x&gclid=y" or "utm_source=x&gclid=y")
// into a flat { key: value } object. Skips empty/malformed pairs and runs
// decodeURIComponent on both sides so values like "cid%7C23258764253" come
// back human-readable. Returns {} for falsy/empty input.
function parseUtmParams(utmString) {
  return (utmString || "")
    .replace(/^\?/, "")
    .split("&")
    .reduce(function (acc, param) {
      var parts = param.split("=")
      var key = parts[0]
      var value = parts[1]
      if (key && value) acc[decodeURIComponent(key)] = decodeURIComponent(value)
      return acc
    }, {})
}

// Build the campaign super-property bag that we register on every PostHog
// event. Single source of truth shared by `apps/frontend` (logged-in app)
// and `apps/landing` (marketing site) so the schema for first-touch /
// last-touch attribution stays in sync across the funnel.
//
// `li_fat_id` is special: LinkedIn's Insight Tag (loaded via GTM) drops a
// first-party cookie called `li_fat_id` directly, which survives internal
// navigations. The cookie is the preferred source; URL/UTM fallback only
// matters if a campaign explicitly appends `?li_fat_id=...`.
// `oppref` (OpenAI Ads click id) is the inverse of li_fat_id: its canonical
// delivery is the ad landing URL param (captured into the UTM string), so
// that wins; the `__oppref` cookie the OpenAI pixel writes is only the
// fallback for when our URL capture missed the touch. Cookie-first here
// would leak a stale click id from a previous ad click, because the pixel
// loads asynchronously and refreshes its cookie AFTER we register.
function buildAttributionProps(utmInput) {
  // Accept either a raw query string (legacy) or a pre-parsed dict.
  var utmParams = typeof utmInput === "string" ? parseUtmParams(utmInput) : utmInput || {}
  var liFatIdCookie = getCookie("li_fat_id") || ""
  var opprefCookie = getCookie("__oppref") || ""

  return {
    utm_source: utmParams.utm_source || "",
    utm_medium: utmParams.utm_medium || "",
    utm_campaign: utmParams.utm_campaign || "",
    utm_content: utmParams.utm_content || "",
    utm_term: utmParams.utm_term || "",
    gclid: utmParams.gclid || "",
    fbclid: utmParams.fbclid || "",
    msclkid: utmParams.msclkid || "",
    ttclid: utmParams.ttclid || "",
    li_fat_id: liFatIdCookie || utmParams.li_fat_id || "",
    oppref: utmParams.oppref || opprefCookie || "",
    gad_source: utmParams.gad_source || "",
    gad_campaignid: utmParams.gad_campaignid || "",
  }
}

// Register attribution into PostHog. Sets super-properties (stamped on
// every subsequent event) via `register()` and first-touch person
// properties via `register_once()`. Idempotent - safe to call on every
// page load. Pass `extra` for caller-specific super-props (ga_client_id,
// version, app_language), and `referrer` / `url` for the first-touch
// referrer + landing page recorded once.
function registerPostHogAttribution(posthog, utmString, opts) {
  if (!posthog || typeof posthog.register !== "function") return
  opts = opts || {}

  var utmParams = parseUtmParams(utmString)
  var phCampaignProps = buildAttributionProps(utmParams)

  // Start from the canonical campaign bag, then layer in any non-utm_* params
  // we received raw (gbraid, wbraid, ucc, campaign_id, etc.) so we preserve
  // click ids that aren't enumerated in phCampaignProps.
  var registerPayload = {}
  Object.keys(phCampaignProps).forEach(function (key) {
    registerPayload[key] = phCampaignProps[key]
  })
  Object.keys(utmParams).forEach(function (key) {
    if (key.indexOf("utm_") !== 0 && !(key in phCampaignProps)) {
      registerPayload[key] = utmParams[key]
    }
  })
  // caller-specific extras (ga_client_id, version, app_language, etc.)
  if (opts.extra) {
    Object.keys(opts.extra).forEach(function (key) {
      if (opts.extra[key] !== undefined && opts.extra[key] !== "") {
        registerPayload[key] = opts.extra[key]
      }
    })
  }

  posthog.register(registerPayload)

  posthog.register_once({
    initial_utm_source: phCampaignProps.utm_source,
    initial_utm_medium: phCampaignProps.utm_medium,
    initial_utm_campaign: phCampaignProps.utm_campaign,
    initial_utm_content: phCampaignProps.utm_content,
    initial_utm_term: phCampaignProps.utm_term,
    initial_gad_campaignid: phCampaignProps.gad_campaignid,
    initial_li_fat_id: phCampaignProps.li_fat_id,
    initial_referrer: opts.referrer || "$direct",
    initial_landing_page: opts.url || "",
  })
}

module.exports = {
  parseUtmParams,
  buildAttributionProps,
  registerPostHogAttribution,
}
